import { useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ViewSelector } from './view-selector';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { DownloadFormat, StyleFormat } from '../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useCreateDocChronologyFromChronology } from '@/api/mutations/useCreateDocChronologyFromChronology';
import useChronologyDownload from '@/api/mutations/useDownloadChronology';
import { PermissionGate } from '@/hooks/useHasPermission';
import { DownloadFlow } from '@/new/components/download/DownloadFlow';
import type { AsyncVariant, DownloadDefinition, DownloadVariant } from '@/new/components/download/types';
import { useDownloadController } from '@/new/components/download/useDownloadController';
import { Icon } from '@/new/components/icon';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';

enum ViewMode {
  Table = 'table',
  Visual = 'visual',
}

interface ChronologyTableToolbarProps {
  chronologyTitle: string;
  totalCount?: number;
  goToPage: (value: number) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  canGetPrevPage: boolean;
  canGetNextPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages: number | undefined;
}

export function ChronologyTimelineHeader({
  chronologyTitle,
  totalCount,
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  goToPage,
  viewMode,
  onViewModeChange,
}: ChronologyTableToolbarProps) {
  const { chronologyId, matterId, subsetId } = useParams();
  if (!chronologyId || !matterId || !subsetId) return null;

  const navigate = useNavigate();

  const download = useDownloadController();

  const { mutate: createDocChronologyFromChronology, isLoading: isCreatingDocChronology } =
    useCreateDocChronologyFromChronology({
      onSuccess: (data) => {
        navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${data.id}`);
      },
      onError: () => {
        toast.error('Failed to create document chronology.');
      },
    });

  const { downloadMutation, downloadExport } = useChronologyDownload({ chronologyId, matterId, subsetId });
  const { mutateAsync: fetchDownload } = downloadMutation;

  const downloadDefinition = useMemo<DownloadDefinition>(() => {
    const progressEndpoint = () =>
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/export-progress`;
    const statusEndpoint = () =>
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/export-status`;
    const downloadReady = async (exportId: string, format?: DownloadFormat, filename?: string) => {
      await downloadExport(filename || chronologyTitle, exportId, format);
    };

    const tableVariant: AsyncVariant<DownloadFormat> = {
      id: 'chronology-table',
      kind: 'table',
      label: 'Table',
      description: 'Word or Excel',
      mode: 'async',
      formats: [DownloadFormat.EXCEL, DownloadFormat.DOCX],
      defaultFilename: chronologyTitle,
      columns: { available: chronologyDownloadColumnOptions, default: chronologyDownloadColumnOptions },
      sourceDocs: true,
      largeExportWarning: true,
      mutate: async (values) => {
        if (values.format === DownloadFormat.EXCEL) {
          trackCustomEvent(TrackingEventsV2.ChronologyDownloadCsv, { chronologyId, subsetId });
        } else if (values.format === DownloadFormat.DOCX) {
          trackCustomEvent(TrackingEventsV2.ChronologyDownloadDocx, { chronologyId, subsetId });
        }
        if (values.includeSourceDocuments) {
          trackCustomEvent(TrackingEventsV2.ChronologyDownloadWithDocuments, { chronologyId, subsetId });
        }
        return fetchDownload({
          title: values.filename,
          columns: values.columns,
          styleFormat: StyleFormat.Table,
          downloadFormat: values.format,
          includeSourceDocuments: values.includeSourceDocuments,
          orderByDate: values.orderByDate,
        });
      },
      progressEndpoint,
      statusEndpoint,
      downloadReady,
    };

    const writtenVariant: AsyncVariant<DownloadFormat> = {
      id: 'chronology-written',
      kind: 'written',
      label: 'Written',
      description: 'Word',
      mode: 'async',
      formats: [DownloadFormat.DOCX],
      defaultFilename: chronologyTitle,
      style: true,
      styleItemNoun: 'fact',
      sourceDocs: true,
      largeExportWarning: true,
      mutate: async (values) => {
        trackCustomEvent(TrackingEventsV2.ChronologyDownloadWrittenDocx, { chronologyId, subsetId });
        if (values.style) {
          trackCustomEvent(TrackingEventsV2.ChronologyDownloadWrittenCustom, { chronologyId, subsetId });
        }
        if (values.includeSourceDocuments) {
          trackCustomEvent(TrackingEventsV2.ChronologyDownloadWithDocuments, { chronologyId, subsetId });
        }
        return fetchDownload({
          title: values.filename,
          style: values.style,
          styleFormat: StyleFormat.Written,
          downloadFormat: values.format,
          documentOptions: values.docOptions,
          includeSourceDocuments: values.includeSourceDocuments,
          orderByDate: values.orderByDate,
        });
      },
      progressEndpoint,
      statusEndpoint,
      downloadReady,
    };

    const variants: DownloadVariant[] = [tableVariant, writtenVariant];
    return { totalCount, variants };
  }, [chronologyId, matterId, subsetId, chronologyTitle, totalCount, fetchDownload, downloadExport]);

  return (
    <div className="flex gap-2">
      {viewMode !== ViewMode.Visual ? (
        <div className="flex items-center gap-2">
          <PermissionGate permission={Permission.ChronologyDownload}>
            <Button onClick={download.open}>
              <Icon className="lucide--download" />
              Download
            </Button>
            <DownloadFlow controller={download} definition={downloadDefinition} />
          </PermissionGate>
          <PermissionGate permission={Permission.DocChronologyCreate}>
            <Button
              disabled={isCreatingDocChronology}
              onClick={() => createDocChronologyFromChronology({ matterId, subsetId, chronologyId })}
            >
              {isCreatingDocChronology ? (
                <Icon className="lucide--loader animate-spin ease-in-out" />
              ) : (
                <Icon className="lucide--folder-git-2" />
              )}
              Create as Document Chronology
            </Button>
          </PermissionGate>
        </div>
      ) : (
        <div></div>
      )}

      <div className="flex items-center gap-2">
        <Pagination
          canGetPrevPage={canGetPrevPage}
          canGetNextPage={canGetNextPage}
          prevPage={prevPage}
          nextPage={nextPage}
          currentPage={currentPage}
          noOfPages={noOfPages}
          goToPage={goToPage}
        />

        <ViewSelector viewMode={viewMode} onViewModeChange={onViewModeChange} />
      </div>
    </div>
  );
}

const chronologyDownloadColumnOptions = [
  'Date (verbatim)',
  'Date (standardised)',
  'Fact',
  'Relevance',
  'Source Text',
  'Source Document',
  'Significance',
  'Id',
  'Notes',
  'Notes Date',
  'Notes By',
  'Verified By',
  'Verified Date',
];
